import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Box, Link, TextField, Typography } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { Approver } from "../../types/approveListReponse";

interface DropdownEditorProps {
  value: string;
  api: any;
  node: any;
  approvers?: Approver[];
  plants?: string[];
  materialTypes?: string[];
  onSelect?: (selected: any) => void;
}

const DropdownEditor = forwardRef<any, DropdownEditorProps>((props, ref) => {
  // Sort plants ascending order for dropdown
  const { approvers = [], materialTypes = [], onSelect } = props;
  const plants = Array.isArray(props.plants) ? [...props.plants].sort((a, b) => String(a).localeCompare(String(b))) : [];

  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<Approver | string | null>(null);

  /* -------------------- Focus On Mount -------------------- */
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /* -------------------- Sync Value -------------------- */
  useEffect(() => {
    if (props.value && approvers.length > 0) {
      setValue(approvers.find(a => a.email === props.value) || null);
    } else if (props.value && plants.length > 0) {
      setValue(plants.find(p => p === props.value) || null);
    } else if (props.value && materialTypes.length > 0) {
      setValue(materialTypes.find(m => m === props.value) || null);
    }
  }, [props.value, approvers, plants, materialTypes]);

  /* -------------------- AG Grid: Commit Value -------------------- */
  useImperativeHandle(ref, () => ({
    getValue() {
      if (typeof value === "string") return value;
      return value?.email || "";
    },
  }));

  /* -------------------- Commit + Move Next -------------------- */
  const commitAndMove = () => {
    props.api.stopEditing();
    props.api.tabToNextCell();
  };

  /* -------------------- On Change From Dropdown -------------------- */
  const handleChange = (_: any, newValue: Approver | string | null) => {
    if (!newValue) return;

    setValue(newValue);
    onSelect?.({ ...props.node.data });

    if (typeof newValue === "string") {
      if (plants.length > 0) {
        props.node.setData({ ...props.node.data, Plant: newValue });
      } else {
        props.node.setData({
          ...props.node.data,
          "IO Material Type": newValue,
        });
      }
    } else {
      props.node.setData({
        ...props.node.data,
        Approver: newValue.name,
        "Approver Email Address": newValue.email,
        id: `${newValue.email}_${props.node.data?.id?.split("_")[1]}`,
      });
    }

    commitAndMove();
  };

  /* -------------------- TAB / ENTER Auto‑Select -------------------- */
  const handleAutoSelect = (event: any, inputValue: string) => {
    if (!inputValue) {
      commitAndMove();
      return;
    }

    const lower = inputValue.toLowerCase();

    if (approvers.length > 0) {
      const match = approvers.find(
        a =>
          a.name.toLowerCase().includes(lower) ||
          a.email.toLowerCase().includes(lower)
      );
      if (match) return handleChange(event, match);
    }

    if (plants.length > 0) {
      const match = plants.find(p => p.toLowerCase().includes(lower));
      if (match) return handleChange(event, match);
    }

    if (materialTypes.length > 0) {
      const match = materialTypes.find(m =>
        m.toLowerCase().includes(lower)
      );
      if (match) return handleChange(event, match);
    }

    // Fallback → commit typed value
    setValue(inputValue);
    commitAndMove();
  };

  /* -------------------- Filter Options -------------------- */
  const filterOptions = createFilterOptions<any>({
    stringify: option =>
      typeof option === "string"
        ? option
        : `${option.name} ${option.email}`,
  });

  const noOptionsContent =
    approvers.length > 0 ? (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          maxWidth: 380,
          backgroundColor: "#FFF5F5",
          borderLeft: "3px solid #D32F2F",
          borderRadius: "4px",
        }}
      >
        {/* <Typography fontSize={13} fontWeight={700} color="error.main" sx={{ mb: 0.5 }}>
          Approver access is missing
        </Typography> */}
        <Typography fontSize={12} fontWeight={700} color="text.secondary" sx={{ lineHeight: 1.4 }}>
          The user you are trying to add does not have the Approver Access. <br/> Request the user to first apply the access in{" "}
          <Link
            href={process.env.REACT_APP_USER_ACCESS_MANAGEMENT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={() =>
              window.open(process.env.REACT_APP_USER_ACCESS_MANAGEMENT_URL, "_blank", "noopener,noreferrer")
            }
            sx={{
              fontWeight: 600,
              textDecoration: "underline",
              textUnderlineOffset: "2px",
              color: "error.dark",
              cursor: "pointer",
            }}
          >
            UAM Application
          </Link>
          {" "}in the Autonomous Layer.
        </Typography>
      </Box>
    ) : (
      <Typography fontSize={12} fontWeight={700} color="text.secondary" sx={{ px: 1.5, py: 1 }}>
        No options found
      </Typography>
    );

  return (
    <Autocomplete
      autoFocus
      value={value}
      noOptionsText={noOptionsContent}
      options={
        approvers.length > 0
          ? approvers
          : plants.length > 0
          ? plants
          : materialTypes
      }
      isOptionEqualToValue={(option, val) =>
        typeof option === "string" && typeof val === "string"
          ? option === val
          : option?.email === val?.email
      }
      getOptionLabel={option =>
        typeof option === "string"
          ? option
          : `${option.name} (${option.email})`
      }
      filterOptions={filterOptions}
      popupIcon={null}
      clearIcon={null}
      disableClearable
      onChange={handleChange}
      renderInput={params => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="standard"
          placeholder={
            approvers.length > 0
              ? "Search name or email"
              : plants.length > 0
              ? "Search plant"
              : "Search material type"
          }
          InputProps={{ ...params.InputProps, disableUnderline: true }}
          sx={{ fontSize: 14, height: 32, px: 1 }}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === "Tab") {
              e.preventDefault(); // 🔥 critical
              handleAutoSelect(
                e,
                (e.target as HTMLInputElement).value
              );
            }
            if (e.key === "Escape") {
              e.stopPropagation();
              props.api.stopEditing(true);
            }
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} style={{ padding: "6px 12px" }}>
          <Box>
            <Typography fontSize={14} fontWeight={500}>
              {typeof option === "string" ? option : option.name}
            </Typography>
            {typeof option !== "string" && (
              <Typography fontSize={12} color="text.secondary">
                {option.email}
              </Typography>
            )}
          </Box>
        </li>
      )}
      sx={{
        width: "100%",
        height: "32px",
        "& .MuiAutocomplete-noOptions": {
          p: 0,
          alignItems: "flex-start",
        },
        "& .MuiInputBase-root": {
          height: "32px",
        },
      }}
    />
  );
});

export default DropdownEditor;
